/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { JSX } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  isHTMLElement,
  LexicalCommand,
  LexicalEditor,
} from 'lexical';
import { useEffect, useRef, useState, useContext } from 'react';
import * as React from 'react';

import { $createImageNode, $isImageNode, ImageNode, ImagePayload } from '../../nodes/ImageNode';
import { DialogActions } from '@mui/material';
import useManageAttachment from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageAttachment';
import { useParams } from 'react-router-dom';
import { Button, TextField } from '@mui/material';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import FileUploader, { FileInfo } from 'components/_atoms/FileUploader';
import { useQueryClient } from 'react-query';

export type InsertImagePayload = Readonly<ImagePayload>;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> = createCommand('INSERT_IMAGE_COMMAND');

export function InsertImageUriDialogBody({ onClick }: { onClick: (payload: InsertImagePayload) => void }) {
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');

  const isDisabled = src === '';

  return (
    <>
      <TextField
        label='Image URL'
        placeholder='i.e. https://source.unsplash.com/random'
        value={src}
        onChange={(e) => setSrc(e.target.value)}
        fullWidth
        margin='normal'
        inputProps={{ 'data-test-id': 'image-modal-url-input' }}
      />

      <TextField
        label='Alt Text'
        placeholder='Descriptive alternative text'
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        fullWidth
        margin='normal'
        inputProps={{ 'data-test-id': 'image-modal-alt-text-input' }}
      />
      <DialogActions>
        <Button
          data-test-id='image-modal-confirm-btn'
          disabled={isDisabled}
          onClick={() => onClick({ altText, src })}
        >
          Confirm
        </Button>
      </DialogActions>
    </>
  );
}
type Props = {
  onClick: (payload: InsertImagePayload) => void;
};
export function InsertImageUploadedDialogBody({ onClick }: { onClick: (payload: InsertImagePayload) => void }) {
  const { id: routeManualId } = useParams();
  const manualId = routeManualId ?? 'defaultManualId';
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [altText, setAltText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const { createAttachment, getAttachment, base64ToBlob, uploadFile } = useManageAttachment();
  const { getLock, removeLock } = useManageManual();
  const queryClient = useQueryClient();

  const handleFileChange = (info: FileInfo) => {
    setFileInfo(info);
  };

  const handleConfirm = async () => {
    if (!fileInfo || !manualId) {
      return;
    }
    setIsUploading(true);

    try {
      await new Promise<void>((resolve, reject) => {
        getLock(
          manualId,
          () => {
            resolve();
          },
          (err) => {
            console.error('Failed to lock manual:', err);
            reject(err);
          }
        );
      });
      let uploadUrl: string | null = null;
      let attachmentId: string | null = null;

      await new Promise<void>((resolve, reject) => {
        createAttachment(
          {
            manualId,
            file: new File([fileInfo.data], fileInfo.name, { type: fileInfo.type }),
            name: fileInfo.name,
            size: fileInfo.size,
          },
          (url, id) => {
            uploadUrl = url;
            attachmentId = id;
            resolve();
          },
          (error) => {
            console.error('createAttachment failed:', error);
            reject(error);
          }
        );
      });

      if (!uploadUrl || !attachmentId) {
        throw new Error('Presigned URL or attachment ID is missing');
      }
      const blob = base64ToBlob(fileInfo.data, fileInfo.type);
      const file = new File([blob], fileInfo.name, { type: fileInfo.type });
      await uploadFile(uploadUrl, file);
      const attachment = await getAttachment({ manualId, attachmentId });
      onClick({
        altText,
        src: attachment.url,
        manualId,
        attachmentId,
      });
    } catch (err) {
      console.error('Error in lock/create/upload flow:', err);
    } finally {
      await new Promise<void>((resolve, reject) => {
        removeLock(
          manualId,
          () => {
            queryClient.invalidateQueries(['draft_manual', manualId]);
            resolve();
          },
          (error) => {
            console.error('Failed to remove lock:', error);
            reject(error);
          }
        );
      });
      setIsUploading(false);
    }
  };

  const isDisabled = !fileInfo || isUploading;

  return (
    <>
      <FileUploader
        onChange={handleFileChange}
        onError={(errors) => {
          console.error('File upload errors:', errors);
        }}
        placeholder='Drag or click to select an image'
      />
      <TextField
        label='Alt Text'
        placeholder='Descriptive alternative text'
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        fullWidth
        margin='normal'
      />

      <DialogActions>
        <Button
          disabled={isDisabled}
          onClick={handleConfirm}
        >
          {isUploading ? 'Uploading...' : 'Confirm'}
        </Button>
      </DialogActions>
    </>
  );
}

export function InsertImageDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): JSX.Element {
  const onClick = (payload: InsertImagePayload) => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    onClose();
  };

  return <InsertImageUploadedDialogBody onClick={onClick} />;
}

export default function ImagesPlugin({ captionsEnabled }: { captionsEnabled?: boolean }): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<InsertImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event);
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [captionsEnabled, editor]);

  return null;
}

const TRANSPARENT_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
        manualId: node.__manualId,
        attachmentId: node.__attachmentId,
      },
      type: 'image',
    })
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
  }
  return true;
}

function $getImageNodeInSelection(): ImageNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertImagePayload {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== 'image') {
    return null;
  }

  return data;
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}

function canDropImage(event: DragEvent): boolean {
  const { target } = event;
  // If target is null or not an element, bail out
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  return !!(
    // isHTMLElement(target) is basically checking (target instanceof HTMLElement)
    (
      !target.closest('code, span.editor-image') &&
      target.parentElement &&
      target.parentElement.closest('div.ContentEditable__root')
    )
  );
}

function getDragSelection(event: DragEvent): Range | null {
  let range: Range | null = null;
  const domSelection = window.getSelection();
  // or document.getSelection(), either is fine

  if (!domSelection) {
    return null;
  }

  // For older browsers that support caretRangeFromPoint:
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent) {
    domSelection.collapse(event.rangeParent, event.rangeOffset ?? 0);
    range = domSelection.rangeCount > 0 ? domSelection.getRangeAt(0) : null;
  } else {
    throw Error('Cannot get the selection when dragging');
  }

  return range;
}
